import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, TouchableOpacity } from 'react-native';

import Avatar, { AvatarProps } from '@components/Avatar/Avatar';
import CachedImage from '@components/CachedImage/CachedImage';
import Highlighter from '@components/HighlightWords';
import MemberListSummary from '@components/MemberListSummary/MemberListSummary';
import { BoxHover } from '@components/Restyle/BoxHover.web';
import { Box, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import { LocalFile, Project, ProjectMember } from '@graphql/generated';
import useNewColors from '@hooks/useNewColors';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { useAppNavigation } from '@navigation/useAppNavigation';
import Images from '@themes/images';
import theme from '@themes/theme';

interface ProjectCardProps {
  filterVal?: string;
  expanded?: boolean;
  project: Project;
  onClick: (project: Project) => void;
  onDelete?: () => void;
  onUnPinClick?: () => void;
  disabled?: boolean;
  suffixText?: string;
  width?: number;
  alphabetize?: boolean;
  alphabetical?: boolean;
  isRecent?: boolean;
  isGlobalSearch?: boolean;
}

type ProjectMemberStripSource = Partial<
  Pick<Project, 'members' | 'memberPreviews' | 'usersCount'>
>;

/**
 * Members for the avatar strip. Queries that still select the full (deprecated)
 * `members` list use it as-is. Queries that select the bounded
 * `memberPreviews` instead (e.g. `search`, TA-10497) also select `usersCount`,
 * which is then the real total for the "+N" counter.
 */
function getProjectMemberStrip(project: ProjectMemberStripSource): {
  members: ProjectMember[];
  totalCount?: number;
} {
  if (project.members) return { members: project.members };
  if (project.memberPreviews) {
    return {
      members: project.memberPreviews,
      totalCount: project.usersCount,
    };
  }
  return { members: [] };
}

const ProjectCard = ({
  filterVal = '',
  expanded = true,
  project,
  onClick,
  onDelete,
  onUnPinClick,
  disabled,
  suffixText,
  width,
  alphabetize,
  alphabetical,
  isRecent,
  isGlobalSearch,
}: ProjectCardProps) => {
  const navigation = useAppNavigation();
  const {
    color,
    icon,
    name,
    description,
    creator,
    archivedAt,
    settings,
  }: Project = project;
  const { pin } = settings || {};
  const memberStrip = getProjectMemberStrip(project);
  const { t } = useTranslation('shared');
  const { selectedProject, setSelectedProject } = useWebDrawerNavigator();

  const { getMappedColor } = useNewColors();

  const [teamAndOwnerWidth, setTeamAndOwnerWidth] = useState(0);
  const [teamWidth, setTeamWidth] = useState(0);
  const [ownerWidth, setOwnerWidth] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isHoveredTitle, setIsHoveredTitle] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const mainWidth = Dimensions.get('window').width;
  const teamOwnerSplitWidth =
    teamWidth > 0 || ownerWidth > 0
      ? ((teamAndOwnerWidth - theme.spacing.xs) * teamWidth) /
        (teamWidth + ownerWidth)
      : teamAndOwnerWidth / 2;
  const maxTeamWidth = creator ? teamOwnerSplitWidth : teamAndOwnerWidth;
  const maxOwnerWidth =
    teamWidth > 0 || ownerWidth > 0
      ? ((teamAndOwnerWidth - theme.spacing.xs) * ownerWidth) /
        (teamWidth + ownerWidth)
      : teamAndOwnerWidth / 2;
  const minWidth =
    ((Dimensions.get('window').width <= 1280 ? 639 : 742) -
      theme.spacing.l * 2) /
      4 -
    theme.spacing.m;

  const bgHovered = isHovered ? 'grey01' : 'white';

  const getNameAndAddressContainer = () => {
    const bgHovered = isHovered ? undefined : 'grey01';
    return (
      <Box>
        <Box flexDirection='row' alignItems='center'>
          <Box
            alignItems='center'
            justifyContent='center'
            marginRight='xs'
            style={{
              borderRadius: 5,
              padding: theme.spacing.xxs,
              backgroundColor: archivedAt
                ? theme.colors.grey03
                : getMappedColor(color),
            }}>
            <Icon
              name={icon ? (icon as keyof typeof Images) : 'BuildingHomeHouse'}
              color='white'
              variant='m'
            />
          </Box>
          {(mainWidth >= 1440 || (mainWidth <= 1280 && isRecent)) && (
            <Box
              flex={1}
              flexDirection='row'
              alignItems='center'
              onMouseEnter={() => setIsHoveredTitle(true)}
              onMouseLeave={() => setIsHoveredTitle(false)}>
              <Text
                numberOfLines={2}
                variant='bodyTaskName'
                ellipsizeMode='tail'
                textDecorationLine={isHoveredTitle ? 'underline' : undefined}>
                <Highlighter
                  autoEscape
                  highlightStyle={{
                    color: theme.colors.greenSecondary,
                    backgroundColor: theme.colors.background,
                  }}
                  searchWords={filterVal?.split(' ') || ''}
                  textToHighlight={name}
                />
              </Text>
            </Box>
          )}
          <Box
            flex={
              mainWidth < 1440 && (mainWidth > 1280 || !isRecent)
                ? 1
                : undefined
            }
            flexDirection='row'
            alignItems='center'
            justifyContent='flex-end'></Box>
        </Box>
        {mainWidth < 1440 && (mainWidth > 1280 || !isRecent) && (
          <Box
            flex={1}
            flexDirection='row'
            alignItems='center'
            marginTop='m'
            onMouseEnter={() => setIsHoveredTitle(true)}
            onMouseLeave={() => setIsHoveredTitle(false)}>
            <Text
              numberOfLines={1}
              variant='bodyTaskName'
              ellipsizeMode='tail'
              textDecorationLine={isHoveredTitle ? 'underline' : undefined}>
              <Highlighter
                autoEscape
                highlightStyle={{
                  color: theme.colors.greenSecondary,
                  backgroundColor: theme.colors.background,
                }}
                searchWords={filterVal?.split(' ') || ''}
                textToHighlight={name}
              />
            </Text>
          </Box>
        )}
        <Box
          flexDirection={
            mainWidth < 1440 && (mainWidth > 1280 || !isRecent)
              ? 'column'
              : 'row'
          }
          alignItems='flex-start'
          marginTop='l'
          onLayout={(e) => setTeamAndOwnerWidth(e.nativeEvent.layout.width)}>
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='flex-start'
            backgroundColor={bgHovered}
            padding='xxs'
            borderRadius='xxs'
            marginRight={creator ? 'xs' : undefined}
            maxWidth={
              mainWidth < 1440 && (mainWidth > 1280 || !isRecent)
                ? teamAndOwnerWidth
                : maxTeamWidth
            }
            onLayout={(e) => setTeamWidth(e.nativeEvent.layout.width)}>
            {project.team?.avatarFile ? (
              <Box marginRight='xs'>
                <CachedImage
                  avatarType='team'
                  avatarId={project.team.avatarFile.id}
                  image={project.team.avatarFile as unknown as LocalFile}
                  width={24}
                  height={24}
                  borderRadius={theme.spacing.xxs}
                />
              </Box>
            ) : (
              <Icon
                marginRight='xs'
                name={project.team?.personal ? 'Office' : 'TTProject'}
                variant='l'
                color={project.team?.personal ? 'white' : undefined}
                backgroundColor={
                  project.team?.personal ? 'greenSecondary' : 'black'
                }
                padding={project.team?.personal ? 'xxs' : 'none'}
                width={project.team?.personal ? 16 : 24}
                height={project.team?.personal ? 16 : 24}
                borderRadius={project.team?.personal ? 'xxs' : 'xxs'}
              />
            )}
            <Text numberOfLines={1} variant='metadata' ellipsizeMode='tail'>
              {project.team?.name}
            </Text>
          </Box>
          {creator && (
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='flex-start'
              backgroundColor={bgHovered}
              padding='xxs'
              borderRadius='xxs'
              marginTop={
                mainWidth < 1440 && (mainWidth > 1280 || !isRecent)
                  ? 'xxs'
                  : undefined
              }
              maxWidth={
                mainWidth < 1440 && (mainWidth > 1280 || !isRecent)
                  ? teamAndOwnerWidth
                  : maxOwnerWidth
              }
              onLayout={(e) => setOwnerWidth(e.nativeEvent.layout.width)}>
              <Box borderWidth={1} borderColor='white' borderRadius='s'>
                <Avatar
                  id={creator.id}
                  avatar={creator.avatar}
                  label={creator.name}
                  size='xs'
                />
              </Box>
              <Text
                variant='metadata'
                color='textPrimary'
                marginLeft='xs'
                letterSpacing={0.2}
                numberOfLines={1}
                ellipsizeMode='tail'>
                {creator.name || ''}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const getNameAndAddressContainerCondensed = () => (
    <Box flexDirection='row' alignItems='center' height={48}>
      <Box
        alignItems='center'
        justifyContent='center'
        marginRight='xs'
        style={{
          borderRadius: 5,
          padding: theme.spacing.xxs,
          backgroundColor: archivedAt
            ? theme.colors.grey03
            : getMappedColor(color),
        }}>
        <Icon
          name={icon ? (icon as keyof typeof Images) : 'BuildingHomeHouse'}
          color='white'
          variant='m'
        />
      </Box>
      <Box flex={mainWidth < 1025 ? 1 : 2}>
        <Box flexDirection='row' alignItems='center' paddingRight='m'>
          <Text
            numberOfLines={1}
            variant={isGlobalSearch ? 'webBodySecondary' : 'labelTableBodyText'}
            ellipsizeMode='tail'
            letterSpacing={0.2}
            textDecorationLine={isHoveredTitle ? 'underline' : undefined}
            onMouseEnter={() => setIsHoveredTitle(true)}
            onMouseLeave={() => setIsHoveredTitle(false)}>
            <Highlighter
              autoEscape
              highlightStyle={{
                color: isGlobalSearch
                  ? theme.colors.textPrimary
                  : theme.colors.greenSecondary,
                backgroundColor: isGlobalSearch
                  ? theme.colors.yellowBright
                  : theme.colors.background,
              }}
              searchWords={filterVal?.split(' ') || ''}
              textToHighlight={name}
            />
          </Text>
          {pin && (
            <Icon
              name='Pin'
              color='grey03'
              variant='xxs'
              marginHorizontal='xs'
              onPress={() => {
                onUnPinClick && onUnPinClick();
              }}
            />
          )}
        </Box>
        {isGlobalSearch && !!filterVal && description && (
          <Text
            mt='xxs'
            numberOfLines={1}
            variant='metadata'
            color='grey05'
            ellipsizeMode='tail'>
            <Highlighter
              autoEscape
              highlightStyle={{
                color: isGlobalSearch
                  ? theme.colors.textPrimary
                  : theme.colors.greenSecondary,
                backgroundColor: isGlobalSearch
                  ? theme.colors.yellowBright
                  : theme.colors.background,
              }}
              searchWords={filterVal?.split(' ') || ''}
              textToHighlight={description ?? ''}
            />
          </Text>
        )}
      </Box>
      {!isGlobalSearch && creator && (
        <Box
          flex={1}
          flexDirection='row'
          alignItems='flex-start'
          paddingRight='m'>
          <Box borderWidth={1} borderColor='white' borderRadius='s'>
            <Avatar
              id={creator.id}
              avatar={creator.avatar}
              label={creator.name}
              size='small'
            />
          </Box>
          <Box flex={1} alignItems='flex-start' justifyContent='center'>
            <Text
              variant='labelTableBodyText'
              marginLeft='xs'
              letterSpacing={0.2}
              numberOfLines={1}
              ellipsizeMode='tail'>
              {creator.name || ''}
            </Text>
            <Text variant='labelRequired' color='grey04' marginLeft='xs'>
              {t('owner')}
            </Text>
          </Box>
        </Box>
      )}
      {isGlobalSearch && (
        <Box
          flex={1}
          flexDirection='row'
          alignItems='center'
          justifyContent='flex-end'>
          <Text
            marginRight='xs'
            numberOfLines={1}
            ellipsizeMode='tail'
            variant='webBodySecondary'>
            {project.team?.name}
          </Text>
          {project.team?.avatarFile ? (
            <CachedImage
              avatarType='team'
              avatarId={project.team.avatarFile.id}
              image={project.team.avatarFile as unknown as LocalFile}
              width={24}
              height={24}
              borderRadius={theme.spacing.xxs}
            />
          ) : (
            <Icon
              name={project.team?.personal ? 'Office' : 'TTProject'}
              variant='l'
              color={project.team?.personal ? 'white' : undefined}
              backgroundColor={
                project.team?.personal ? 'greenSecondary' : 'black'
              }
              padding={project.team?.personal ? 'xxs' : 'none'}
              width={project.team?.personal ? 16 : 24}
              height={project.team?.personal ? 16 : 24}
              borderRadius={project.team?.personal ? 'xxs' : 'xxs'}
            />
          )}
        </Box>
      )}
      {!isGlobalSearch && (
        <>
          <Box
            flex={1}
            flexDirection='row'
            alignItems='center'
            justifyContent='flex-start'
            padding='xxs'
            paddingRight='m'>
            {project.team?.avatarFile ? (
              <Box marginRight='xs'>
                <CachedImage
                  avatarType='team'
                  avatarId={project.team.avatarFile.id}
                  image={project.team.avatarFile as unknown as LocalFile}
                  width={24}
                  height={24}
                  borderRadius={theme.spacing.xxs}
                />
              </Box>
            ) : (
              <Icon
                marginRight='xs'
                name={project.team?.personal ? 'Office' : 'TTProject'}
                variant='l'
                color={project.team?.personal ? 'white' : undefined}
                backgroundColor={
                  project.team?.personal ? 'greenSecondary' : 'black'
                }
                padding={project.team?.personal ? 'xxs' : 'none'}
                width={project.team?.personal ? 16 : 24}
                height={project.team?.personal ? 16 : 24}
                borderRadius={project.team?.personal ? 'xxs' : 'xxs'}
              />
            )}
            <Text
              numberOfLines={1}
              ellipsizeMode='tail'
              variant='labelTableBodyText'
              letterSpacing={0.2}>
              {project.team?.name}
            </Text>
          </Box>
          <Box
            flex={0.7}
            flexDirection='row'
            justifyContent='flex-end'
            paddingRight='m'>
            <MemberListSummary
              avatars={userAvatars(memberStrip.members)}
              totalCount={memberStrip.totalCount}
              onPress={() => navigateToProjectMembers()}
              maxAvatars={3}
            />
          </Box>
        </>
      )}
    </Box>
  );

  const userAvatars = (members: ProjectMember[]): AvatarProps[] => {
    return (
      members?.map((u) => {
        const { user } = u;
        const { name, id, avatar } = user;
        return {
          id: id,
          label: name,
          avatar: avatar,
          size: 'memberList',
        };
      }) || []
    );
  };

  const navigateToProjectMembers = () => {
    navigation.navigateToProject(project);
    setTimeout(() => {
      navigation.navigate('members', { projectId: project.id });
    }, 1500);
  };

  const expandedBox = () => {
    const borderColorHovered = 'grey02';
    return (
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        width={width}
        borderColor={borderColorHovered}
        borderWidth={1}
        backgroundColor={bgHovered}
        borderRadius='xs'
        padding='m'
        minWidth={minWidth}>
        {getNameAndAddressContainer()}
      </Box>
    );
  };
  const condensedBox = () => {
    return (
      <BoxHover
        noItemSeparatorComponent={isGlobalSearch}
        isHovered={isHovered}
        isPressing={isPressing}
        isSelected={selectedProject?.id === project.id}
        alphabetize={alphabetize}
        alphabetical={alphabetical}
        isProject={true}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Box
          marginLeft={isGlobalSearch ? undefined : 'm'}
          paddingHorizontal={isGlobalSearch ? 'l' : undefined}
          flex={1}>
          {getNameAndAddressContainerCondensed()}
        </Box>
        <Box flexDirection='row' alignItems='center' justifyContent='center'>
          {suffixText && (
            <Text variant='buttonLabel' color='black' mr='xs'>
              {suffixText}
            </Text>
          )}

          {onDelete && (
            <Icon
              name='X2'
              variant='l'
              color='black'
              onPress={onDelete}
              accessibilityLabel='Delete'
            />
          )}
        </Box>
      </BoxHover>
    );
  };
  return (
    <Box accessibilityLabel='Project'>
      <TouchableOpacity
        accessibilityLabel={project.name}
        onPress={() => {
          setTimeout(() => {
            onClick && onClick(project);
          }, 500);
        }}
        onPressIn={() => {
          setIsPressing(true);
        }}
        onPressOut={() => {
          setIsPressing(false);
          setSelectedProject(project);
        }}
        disabled={disabled}>
        {expanded ? expandedBox() : condensedBox()}
      </TouchableOpacity>
    </Box>
  );
};

// Compare the member avatar strip (MemberListSummary at line 460). Only the
// fields userAvatars() reads (user id/name/avatar) affect the render, so a
// join/leave or avatar swap must invalidate memoization.
function areProjectMembersEqual(
  prev?: ProjectMember[] | null,
  next?: ProjectMember[] | null
): boolean {
  if (prev === next) return true;
  if (prev == null || next == null) return prev == null && next == null;
  if (prev.length !== next.length) return false;
  return prev.every((member, i) => {
    const a = member.user;
    const b = next[i].user;
    return a.id === b.id && a.name === b.name && a.avatar === b.avatar;
  });
}

function areProjectCardPropsEqual(
  prev: ProjectCardProps,
  next: ProjectCardProps
): boolean {
  const prevProject = prev.project;
  const nextProject = next.project;
  return (
    prevProject.id === nextProject.id &&
    prevProject.name === nextProject.name &&
    prevProject.tasksCount === nextProject.tasksCount &&
    prevProject.activeTasksCount === nextProject.activeTasksCount &&
    prevProject.archivedAt === nextProject.archivedAt &&
    // Icon background + name (lines 104/108, 275/280).
    prevProject.color === nextProject.color &&
    prevProject.icon === nextProject.icon &&
    // Pin badge visibility (line 309).
    prevProject.settings?.pin === nextProject.settings?.pin &&
    // Global-search description snippet (line 321).
    prevProject.description === nextProject.description &&
    // Creator row avatar/name (lines 245/352).
    prevProject.creator?.id === nextProject.creator?.id &&
    prevProject.creator?.name === nextProject.creator?.name &&
    prevProject.creator?.avatar === nextProject.creator?.avatar &&
    // Team row (lines 195/384).
    prevProject.team?.name === nextProject.team?.name &&
    prevProject.team?.personal === nextProject.team?.personal &&
    prevProject.team?.avatarFile?.id === nextProject.team?.avatarFile?.id &&
    // Member avatar strip (line 460).
    areProjectMembersEqual(prevProject.members, nextProject.members) &&
    areProjectMembersEqual(
      prevProject.memberPreviews,
      nextProject.memberPreviews
    ) &&
    prevProject.usersCount === nextProject.usersCount &&
    prev.expanded === next.expanded &&
    prev.disabled === next.disabled &&
    prev.filterVal === next.filterVal &&
    prev.suffixText === next.suffixText &&
    prev.width === next.width &&
    prev.alphabetize === next.alphabetize &&
    prev.alphabetical === next.alphabetical &&
    prev.isRecent === next.isRecent &&
    prev.isGlobalSearch === next.isGlobalSearch &&
    // Delete icon presence (line 534) — undefined -> fn must show the button.
    !!prev.onDelete === !!next.onDelete
  );
}

export {
  areProjectCardPropsEqual,
  areProjectMembersEqual,
  getProjectMemberStrip,
};

export default memo(ProjectCard, areProjectCardPropsEqual);
